import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { AppShell, Burger, Group, Skeleton, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBuildingStore,
  IconCreditCard,
  IconDashboard,
  IconFileInvoice,
  IconHeartRateMonitor,
  IconLanguage,
  IconLogout,
  IconReceiptDollar,
  IconSettings,
  IconShieldLock,
} from '@tabler/icons-react';
import { trpc } from '../../lib/trpc';
import { ImpersonationBanner } from '../../components/ImpersonationBanner';
import { GroupedNav, type NavGroup } from '../../components/GroupedNav';
import { UserMenu } from '../../components/UserMenu';

const CENTRAL_GROUPS: NavGroup[] = [
  {
    id: 'overview',
    label: 'Dashboard',
    leftSection: <IconDashboard size={16} />,
    children: [{ label: 'Overview', to: '/central', matchPrefix: false }],
  },
  {
    id: 'tenants',
    label: 'Tenants',
    leftSection: <IconBuildingStore size={16} />,
    children: [
      { label: 'Vendors', to: '/central/vendors', matchPrefix: true },
      {
        label: 'Subscriptions',
        to: '/central/subscriptions',
        leftSection: <IconCreditCard size={14} />,
      },
      {
        label: 'Manual queue',
        to: '/central/manual-subscriptions',
        leftSection: <IconFileInvoice size={14} />,
      },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    leftSection: <IconReceiptDollar size={16} />,
    children: [{ label: 'Subscription plans', to: '/central/subscription-plans' }],
  },
  {
    id: 'content',
    label: 'Content',
    leftSection: <IconLanguage size={16} />,
    children: [
      { label: 'CMS pages', to: '/central/cms-pages', matchPrefix: true },
      { label: 'Translations', to: '/central/translations' },
      { label: 'Languages', to: '/central/languages' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    leftSection: <IconSettings size={16} />,
    children: [
      { label: 'Configuration', to: '/central/configuration' },
      {
        label: 'WhatsApp webhook health',
        to: '/central/whatsapp-webhook-health',
        leftSection: <IconHeartRateMonitor size={14} />,
      },
      {
        label: 'Facebook webhook health',
        to: '/central/facebook-webhook-health',
        leftSection: <IconHeartRateMonitor size={14} />,
      },
      {
        label: 'Instagram webhook health',
        to: '/central/instagram-webhook-health',
        leftSection: <IconHeartRateMonitor size={14} />,
      },
      {
        label: 'Data deletion',
        to: '/central/data-deletion',
        leftSection: <IconShieldLock size={14} />,
      },
    ],
  },
];

function CentralShell() {
  const [opened, { toggle }] = useDisclosure();
  const navigate = useNavigate();
  const me = trpc.auth.me.useQuery();

  useEffect(() => {
    if (me.isFetched && me.data && !me.data.roles.includes('SuperAdmin')) {
      navigate({ to: '/auth/login' });
    }
  }, [me.isFetched, me.data, navigate]);

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 260, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>LeadSync · Central</Title>
          </Group>
          <UserMenu />
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p={0}>
        <GroupedNav
          scope="central"
          groups={CENTRAL_GROUPS}
          trailer={{
            label: 'Sign out',
            to: '/auth/logout',
            leftSection: <IconLogout size={16} />,
          }}
        />
      </AppShell.Navbar>
      <AppShell.Main>
        <ImpersonationBanner />
        {me.isPending ? <Skeleton height={400} /> : <Outlet />}
      </AppShell.Main>
    </AppShell>
  );
}

export const Route = createFileRoute('/central')({ component: CentralShell });
